import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { NOTICE_KINDS, NOTICE_TEXT_MAX, newId, type NoticeKind } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { NOTICE_KIND_KEY, NOTICE_PROBLEM_KEY, emptyNoticeForm, formOf, groupNotices, noticeDatesText, noticeProblems, updateSet, useNotices, writePayload, type NoticeForm, type NoticeRow } from '../data/courseStatus.ts'
import { useAreas, type AreaRow } from '../data/geo.ts'
import { localDate } from '../lib/time.ts'
import type { Pending } from '../sync/merge.ts'
import { AreaPicker } from '../ui/AreaPicker.tsx'
import { BackLink, Banner, DisabledHint, Field, NotAllowed, PendingBanner, Pill } from '../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { NoticeLine } from '../ui/NoticeLine.tsx'
import { Picto } from '../ui/pictos.tsx'

/** Réglages › État du parcours (M16-B1, docs/M16.md "B1"): the notices to the team. Whoever runs the course writes one (a kind, the areas, a short text in
 *  one language or both, its dates, a first start for a competition) and the team sees it at once — there is no draft and no approval between the
 *  Superintendent and his own team. The three commands are office intents (course_status.manage; sendAndWait, no projector; kept on the device when
 *  the network is away: `PendingBanner`). The page reads the mirror: in force today, to come, over; a retired notice is on no list. "Approuver pour
 *  l'extérieur" (course_notice.approve@1, course_status.publish) is deliberately not offered here: it means "may leave the building" and only B2's
 *  public page reads it (release b, after the owner's answer); the command exists so that the day it is wanted no payload changes.
 *  The printable "État du parcours" for the reception desk is /settings/course-status/print. */
export function CourseStatusPage() {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const lang = i18n.language
  const manage = useWriteAccess('course_status.manage')
  const notices = useNotices(); const areas = useAreas()
  const retire = useWorkshopAction()
  const [editing, setEditing] = useState<'new' | string | null>(null)
  const today = localDate(new Date(), site.timezone)
  const areaById = useMemo(() => new Map((areas ?? []).map((a) => [a.id, a as AreaRow])), [areas])
  if (!manage.permitted) return <NotAllowed permission="course_status.manage" />
  if (!notices || !areas) return <p>{t('common.loading')}</p>
  const groups = groupNotices(notices, today)
  const groupProps = {
    areas: areas as AreaRow[], areaById, lang, today, canEdit: manage.allowed, editing, busy: retire.busy,
    onEdit: (id: string) => { retire.reset(); setEditing(id) }, onDone: () => setEditing(null),
    onRetire: (n: NoticeRow) => void retire.run('course_notice.retire@1', { notice_id: n.id }, 'courseStatus.retired'),
  }
  return (
    <section className="flex flex-col gap-4" data-testid="course-status-page">
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="flag" className="size-7 text-flag" />{t('courseStatus.title')}</h1>
      <p className="meta">{t('courseStatus.help')}</p>
      {manage.readOnlyMode && <Banner tone="warn">{t('common.onlineOnly')}</Banner>}
      <div className="flex flex-col gap-2 sm:flex-row">
        {manage.allowed && editing === null && <button type="button" className="btn btn-primary" onClick={() => { retire.reset(); setEditing('new') }} data-testid="cs-new">{t('courseStatus.new')}</button>}
        {/* the route is declared in router.tsx by Z's close (docs/m16-requests/b1.md): the casts go the day it is */}
        <Link to="/settings/course-status/print" search={{ d: today }} className="btn" data-testid="cs-print">{t('courseStatus.print')}</Link>
      </div>
      {retire.pending && <PendingBanner />}
      {retire.error && <Banner tone="error">{retire.error}</Banner>}
      {retire.ok && <Banner tone="ok" testId="cs-retired">{retire.ok}</Banner>}
      {editing === 'new' && <NoticeEditor areas={areas as AreaRow[]} lang={lang} today={today} row={null} onDone={() => setEditing(null)} />}
      {notices.every((n) => n.status !== 'live') && <p className="meta" data-testid="cs-empty">{t('courseStatus.none')}</p>}
      <NoticeGroup title={t('courseStatus.current')} items={groups.current} testId="cs-current" {...groupProps} />
      <NoticeGroup title={t('courseStatus.upcoming')} items={groups.upcoming} testId="cs-upcoming" {...groupProps} />
      {groups.past.length > 0 && (
        <details className="flex flex-col gap-2" data-testid="cs-past-details">
          <summary className="flex min-h-14 cursor-pointer items-center text-lg font-bold">{t('courseStatus.past')} ({groups.past.length})</summary>
          <NoticeGroup title="" items={groups.past} testId="cs-past" {...groupProps} />
        </details>
      )}
    </section>
  )
}

/** One group of notices (now, coming, past): the line, the dates, and for whoever manages them the edit and retire controls. A module-level component,
 *  so that a live-query re-render of the page never remounts an editor open on a row (its typed text would be lost). */
function NoticeGroup({ title, items, testId, areas, areaById, lang, today, canEdit, editing, busy, onEdit, onRetire, onDone }: {
  title: string; items: Array<Pending<NoticeRow>>; testId: string; areas: AreaRow[]; areaById: Map<string, AreaRow>; lang: string; today: string
  canEdit: boolean; editing: string | null; busy: boolean; onEdit: (id: string) => void; onRetire: (n: NoticeRow) => void; onDone: () => void
}) {
  const { t } = useTranslation()
  if (items.length === 0) return null
  return (
    <section className="flex flex-col gap-2" data-testid={testId}>
      {title && <h2 className="text-lg font-bold">{title}</h2>}
      <ul className="flex flex-col gap-3">
        {items.map((n) => (
          <li key={n.id} className="card flex flex-col gap-2" data-testid="cs-row" data-notice-id={n.id} data-kind={n.kind}>
            <NoticeLine notice={n} areaById={areaById} lang={lang} />
            <p className="meta flex flex-wrap items-center gap-2">
              <span data-testid="cs-dates">{noticeDatesText(n, lang, t)}</span>
              {n._pending && <Pill tone="amber">{t('common.pendingSend')}</Pill>}
            </p>
            {canEdit && editing !== n.id && (
              <div className="flex flex-wrap gap-2">
                <button type="button" className="btn" disabled={busy} onClick={() => onEdit(n.id)} data-testid="cs-edit">{t('courseStatus.edit')}</button>
                <button type="button" className="btn" disabled={busy} onClick={() => onRetire(n)} data-testid="cs-retire">{t('courseStatus.retire')}</button>
              </div>
            )}
            {editing === n.id && <NoticeEditor areas={areas} lang={lang} today={today} row={n} onDone={onDone} />}
          </li>
        ))}
      </ul>
    </section>
  )
}

/** The form of one notice: new (course_notice.write@1 with a fresh id, renewed only once applied) or an edit (course_notice.update@1 with the revision read,
 *  only what changed). The kind as 56-px cards, the text in one language or both (600 code points at most, counted), the areas with the course's
 *  picker (a loop is one area), the dates, the first start; the primary says what is missing beside it (M23 item 6). A command kept on the device
 *  (no network) shows the amber banner and holds the primary: a second tap would send the same expected revision again. */
function NoticeEditor({ areas, lang, today, row, onDone }: { areas: AreaRow[]; lang: string; today: string; row: NoticeRow | null; onDone: () => void }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const id = useRef(newId())
  const [f, setF] = useState<NoticeForm>(() => (row ? formOf(row) : emptyNoticeForm(today)))
  // an edit kept on the device closes when the row comes back with its new revision (the fonctions' rule, M23 item 10)
  useEffect(() => { if (w.pending && row) onDone() }, [row?.revision])                                            // eslint-disable-line react-hooks/exhaustive-deps
  const problems = noticeProblems(f)
  const set = row ? updateSet(f, row) : null
  const canSave = problems.length === 0 && (row ? set !== null : true) && !w.busy && !w.pending
  const count = (s: string) => [...s].length
  const submit = async () => {
    if (row) {
      if (!set) return
      if (await w.run('course_notice.update@1', { notice_id: row.id, expected_revision: row.revision ?? 1, set }, 'courseStatus.updated')) onDone()
    } else if (await w.run('course_notice.write@1', writePayload(f, id.current), 'courseStatus.saved')) { id.current = newId(); onDone() }
  }
  const suffix = row ? row.id : 'new'
  return (
    <form className="card flex flex-col gap-4" data-testid="cs-form" onSubmit={(e) => { e.preventDefault(); void submit() }}>
      <h2 className="text-lg font-bold">{row ? t('courseStatus.edit') : t('courseStatus.new')}</h2>
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-semibold">{t('courseStatus.kindLabel')}</legend>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {NOTICE_KINDS.map((k: NoticeKind) => (
            <button key={k} type="button" aria-pressed={f.kind === k} onClick={() => setF({ ...f, kind: k })} data-testid={`cs-kind-${k}`}
              className={`btn ${f.kind === k ? 'btn-primary' : ''}`}>{t(NOTICE_KIND_KEY[k])}</button>
          ))}
        </div>
        <p className="meta">{t('courseStatus.kindHelp')}</p>
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('courseStatus.textFr')} htmlFor={`cs-fr-${suffix}`} hint={t('courseStatus.chars', { n: count(f.fr), max: NOTICE_TEXT_MAX })}>
          <textarea id={`cs-fr-${suffix}`} className="input min-h-28" rows={3} maxLength={NOTICE_TEXT_MAX} value={f.fr} onChange={(e) => setF({ ...f, fr: e.target.value })} data-testid="cs-fr" />
        </Field>
        <Field label={t('courseStatus.textEn')} htmlFor={`cs-en-${suffix}`} hint={t('courseStatus.chars', { n: count(f.en), max: NOTICE_TEXT_MAX })}>
          <textarea id={`cs-en-${suffix}`} className="input min-h-28" rows={3} maxLength={NOTICE_TEXT_MAX} value={f.en} onChange={(e) => setF({ ...f, en: e.target.value })} data-testid="cs-en" />
        </Field>
      </div>
      <p className="meta">{t('courseStatus.textHint')}</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={t('courseStatus.startsOn')} htmlFor={`cs-from-${suffix}`}>
          <input id={`cs-from-${suffix}`} type="date" className="input" required value={f.startsOn} onChange={(e) => setF({ ...f, startsOn: e.target.value, endsOn: f.endsOn < e.target.value ? e.target.value : f.endsOn })} data-testid="cs-from" />
        </Field>
        <Field label={t('courseStatus.endsOn')} htmlFor={`cs-to-${suffix}`}>
          <input id={`cs-to-${suffix}`} type="date" className="input" required min={f.startsOn} value={f.endsOn} onChange={(e) => setF({ ...f, endsOn: e.target.value })} data-testid="cs-to" />
        </Field>
        <Field label={t('courseStatus.startTime')} htmlFor={`cs-time-${suffix}`} hint={t('courseStatus.startTimeHint')}>
          <input id={`cs-time-${suffix}`} type="time" className="input" value={f.startTime} onChange={(e) => setF({ ...f, startTime: e.target.value })} data-testid="cs-time" />
        </Field>
      </div>
      {/* M16a review: the picker lists every area of the course (~140 rows): folded, the summary says what is chosen, and Enregistrer stays within a screen */}
      <details className="flex flex-col gap-2" data-testid="cs-areas" data-count={f.areaIds.length}>
        <summary className="flex min-h-14 cursor-pointer flex-col justify-center" data-testid="cs-areas-toggle">
          <span className="font-semibold">{t('courseStatus.areas')} · {f.areaIds.length === 0 ? t('courseStatus.areasNoneChosen') : t('courseStatus.areasChosen', { count: f.areaIds.length })}</span>
          <span className="meta">{t('courseStatus.areasHint')}</span>
        </summary>
        <AreaPicker areas={areas} kinds={[]} value={f.areaIds} onChange={(ids) => setF({ ...f, areaIds: ids })} lang={lang} />
      </details>
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error" testId="cs-error">{w.error}</Banner>}
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn btn-primary" disabled={!canSave} data-testid="cs-save">{t('courseStatus.save')}</button>
        <button type="button" className="btn" disabled={w.busy} onClick={onDone} data-testid="cs-cancel">{t('common.cancel')}</button>
      </div>
      <DisabledHint show={problems.length > 0} testId="cs-hint">{t(NOTICE_PROBLEM_KEY[problems[0] ?? 'text'])}</DisabledHint>
    </form>
  )
}
